import { useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { releaseTask, reviewTask } from '@mkcore/agents/schema';
import { Badge, Button, Card, ErrorState, Field, PageHeader, Skeleton, Textarea, buttonVariants } from '@mkcore/ui';
import { Markdown } from '@/components/markdown';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { ClaimStatusBadge, claimQuery } from '@/features/agents/claims';

export const Route = createFileRoute('/_app/agents/tasks/$id')({ component: ClaimPage });

/**
 * One claim (CO-1232, CO-0430): the work item, the acceptance criteria and expected result, the evidence with the state
 * of pull requests and commits, and the review by a person: approve, ask for changes, or reject.
 */
function ClaimPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const canReview = useCan('agents:update');
  const canManage = useCan('agents:manage');
  const me = useMe().id;
  const q = useQuery(claimQuery(id));
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const review = useActionMutation(reviewTask, { silentErrors: true, invalidate: [['agent', 'task']], success: t('agents.claims.reviewed') });
  const release = useActionMutation(releaseTask, { invalidate: [['agent', 'task']], success: t('agents.claims.released') });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const c = q.data;
  const decide = (decision: 'approve' | 'changes' | 'reject') => review.mutate({ input: { id, decision, ...(comment.trim() && { comment: comment.trim() }) } }, { onError: (e) => setError(message(e)), onSuccess: () => (setComment(''), setError(null)) });
  const open = c.status === 'active' || c.status === 'in_review';
  return (
    <>
      <Link to="/agents/tasks" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('agents.claims.title')}
      </Link>
      <PageHeader
        eyebrow={t(`agents.claims.items.${c.itemType}`)}
        title={c.item ? `${c.item.key} ${c.item.title}` : t('agents.claims.hiddenItem')}
        actions={
          <div className="flex flex-wrap gap-2">
            {c.item && (
              <a href={c.item.url} className={buttonVariants({ variant: 'secondary' })}>
                <ExternalLink /> {t('agents.claims.openItem')}
              </a>
            )}
            {open && (canManage || c.principalId === me) && (
              <Button variant="ghost" loading={release.isPending} onClick={() => release.mutate({ input: { id } })}>
                {t('agents.claims.release')}
              </Button>
            )}
          </div>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <ClaimStatusBadge status={c.status} />
        <span>{t('agents.claims.holder', { name: c.holderName })}</span>
        {c.holderKind === 'agent' && <Badge tone="info">{t('agents.claims.agent')}</Badge>}
        {c.leaseUntil && <span className="text-fg-3">{t('agents.claims.lease', { date: formatDateTime(c.leaseUntil) })}</span>}
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid content-start gap-4">
          <Section label={t('agents.claims.criteria')}>{c.acceptanceCriteria.trim() ? <Markdown text={c.acceptanceCriteria} /> : <p className="text-sm text-fg-3">{t('agents.claims.noCriteria')}</p>}</Section>
          {c.expectedResult.trim() && (
            <Section label={t('agents.claims.expected')}>
              <Markdown text={c.expectedResult} />
            </Section>
          )}
          <Section label={t('agents.claims.evidence')}>
            {!c.evidence.length ? (
              <p className="text-sm text-fg-3">{t('agents.claims.noEvidence')}</p>
            ) : (
              <ul className="grid gap-3">
                {c.evidence.map((e, i) => (
                  <li key={i} className="grid gap-1 border-b border-border pb-2 last:border-0">
                    <span className="flex flex-wrap items-center gap-2 text-sm">
                      <Badge tone="outline">{t(`agents.claims.kinds.${e.kind}`)}</Badge>
                      <span className="font-bold">{e.title}</span>
                      {e.ref && (e.status?.url ? <a href={e.status.url} className="underline">{e.ref}</a> : <span className="text-fg-3">{e.ref}</span>)}
                      {e.status?.ci && <Badge tone={e.status.ci === 'success' ? 'success' : e.status.ci === 'pending' ? 'warning' : 'danger'}>CI {e.status.ci}</Badge>}
                      {e.status?.merged && <Badge tone="success">{t('agents.claims.merged')}</Badge>}
                    </span>
                    {e.summary && <pre className="max-h-48 overflow-auto whitespace-pre-wrap text-xs text-fg-2">{e.summary}</pre>}
                    <span className="text-xs text-fg-3">
                      {e.byName} · {formatDateTime(e.at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
        <div className="grid content-start gap-4">
          {c.review && (
            <Section label={t('agents.claims.review')}>
              <p className="text-sm">
                <strong>{t(`agents.claims.decisions.${c.review.decision}`)}</strong> · {c.review.byName} · {formatDateTime(c.review.at)}
              </p>
              {c.review.comment && <p className="text-sm text-fg-2">{c.review.comment}</p>}
            </Section>
          )}
          {c.status === 'in_review' && canReview && (
            <Section label={t('agents.claims.decide')}>
              <Field label={t('agents.claims.comment')} hint={t('agents.claims.commentHint')}>
                {(p) => <Textarea {...p} rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />}
              </Field>
              {error && (
                <p role="alert" className="text-sm text-danger">
                  {error}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button loading={review.isPending} onClick={() => decide('approve')}>
                  {t('agents.claims.decisions.approve')}
                </Button>
                <Button variant="secondary" disabled={!comment.trim()} onClick={() => decide('changes')}>
                  {t('agents.claims.decisions.changes')}
                </Button>
                <Button variant="danger" disabled={!comment.trim()} onClick={() => decide('reject')}>
                  {t('agents.claims.decisions.reject')}
                </Button>
              </div>
            </Section>
          )}
        </div>
      </div>
    </>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Card role="region" aria-label={label} className="grid gap-2 p-4">
      <h2 className="text-sm font-bold">{label}</h2>
      {children}
    </Card>
  );
}
